import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { createIncidentTicket, attachFileToTicket } from '../api/odoo';
import { safeErrorMessage } from '../utils/errorMessage';
import usePassengerDetails from '../hooks/usePassengerDetails';
import PassengerDetailsCard from '../components/PassengerDetailsCard';
import '../styles/form-page.css';
import './IncidentPage.css';

const MAX_FILE_BYTES = 5 * 1024 * 1024;

const ISSUES = [
  { icon: '🧹', label: 'Room Cleanliness Issues' },
  { icon: '❄️', label: 'Air Conditioning or Ventilation Problems' },
  { icon: '🚿', label: 'Plumbing Problems (e.g., Leaks, Low Water Pressure)' },
  { icon: '💡', label: 'Electrical or Lighting Malfunctions' },
  { icon: '🔑', label: 'Access Card or Door Lock Issues' },
  { icon: '📝', label: 'Other or None of the Above' },
];

export default function IncidentPage({ partner, onBack, onSuccess }) {
  const details = usePassengerDetails();
  const [selected, setSelected] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const { values, isComplete: step1Done } = details;
  const nextFileUid = useRef(0);

  function onFilesSelected(e) {
    const picked = Array.from(e.target.files).slice(0, 3);
    const tooBig = picked.filter((file) => file.size > MAX_FILE_BYTES);
    if (tooBig.length > 0) {
      setError(`${tooBig.map((f) => f.name).join(', ')} exceed${tooBig.length === 1 ? 's' : ''} the 5MB limit.`);
    }
    const accepted = picked.filter((file) => file.size <= MAX_FILE_BYTES);
    setFiles(accepted.map((file) => ({ file, uid: nextFileUid.current++ })));
  }

  async function submit() {
    if (!step1Done) { setShowErrors(true); setError('Please fill in all required fields.'); return; }
    if (!selected) { setError('Please select an issue type.'); return; }
    if (!description.trim()) { setError('Please describe the incident.'); return; }
    if (!agreed) { setError('Please agree to the declaration before submitting.'); return; }
    setLoading(true); setError('');
    try {
      const ticket = await createIncidentTicket(partner, {
        issueType: selected,
        description: description.trim(),
        ticketNo: values.ticketNo,
        pnr: values.pnr,
      });
      const results = await Promise.allSettled(files.map(({ file }) => attachFileToTicket(ticket.id, file)));
      const failedUploads = results
        .map((r, i) => (r.status === 'rejected' ? files[i].file.name : null))
        .filter(Boolean);
      if (failedUploads.length > 0) {
        setError(`Report submitted, but ${failedUploads.length} attachment(s) failed to upload: ${failedUploads.join(', ')}`);
        setLoading(false);
        return;
      }
      onSuccess({ ticketId: ticket.id, ticketName: ticket.name, type: 'incident' });
    } catch (e) { setError(safeErrorMessage(e, 'Submission failed.')); setLoading(false); }
  }

  return (
    <div className="ip">
      <div className="ip-nav">
        <button type="button" className="ip-back" onClick={onBack}>‹</button>
        <div><p className="ip-nav-t">Report Incident</p><p className="ip-nav-s">Tell us what happened</p></div>
      </div>

      <div className="ip-body">

        <PassengerDetailsCard classPrefix="ip" partner={partner} details={details} showErrors={showErrors} />

        {step1Done && (
          <>
            <div className="ip-card">
              <div className="ip-ch"><div className="ip-ci">🚨</div><span className="ip-ct">Issue Type <span className="ip-req">*</span></span></div>
              {ISSUES.map(({ icon, label }) => (
                <button key={label} type="button" className={`ip-row${selected === label ? ' on' : ''}`}
                  aria-pressed={selected === label}
                  onClick={() => { setSelected(label); setError(''); }}>
                  <span className="ip-rico" aria-hidden>{icon}</span>
                  <span className="ip-rtxt">{label}</span>
                  <span className={`ip-rdot${selected === label ? ' on' : ''}`}>{selected === label ? '✓' : ''}</span>
                </button>
              ))}
            </div>

            <div className="ip-card">
              <div className="ip-ch"><div className="ip-ci">📋</div><span className="ip-ct">Description <span className="ip-req">*</span></span></div>
              <textarea className="ip-txt" rows={4} placeholder="Describe what happened in detail…"
                value={description} onChange={e => { setDescription(e.target.value); setError(''); }} />
            </div>

            <div className="ip-card">
              <div className="ip-ch"><div className="ip-ci">📎</div><span className="ip-ct">Attachments</span></div>
              <p className="ip-att-lbl">Upload up to 3 images, max 5MB each (Optional)</p>
              <label className="ip-att-btn" htmlFor="ip-files">📷 Choose Images</label>
              <input id="ip-files" type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={onFilesSelected} />
              <div className="ip-att-pre">
                {files.length === 0
                  ? '📁 No files selected'
                  : files.map(({ file, uid }) => <span key={uid} className="ip-chip">📷 {file.name}</span>)}
              </div>
            </div>
          </>
        )}

        {!step1Done && (
          <div className="ip-step-hint">
            <span aria-hidden>👆</span>
            <p>Fill in passenger details above to unlock the incident form</p>
          </div>
        )}

        {error && <p className="ip-err">{error}</p>}
      </div>

      <div className="ip-foot">
        <label className="ip-agree">
          <input type="checkbox" className="ip-agree-cb" checked={agreed} onChange={e => { setAgreed(e.target.checked); setError(''); }} />
          <span>I confirm that the information provided is accurate.</span>
        </label>
        <button type="button" className="ip-sub" onClick={submit} disabled={loading || !step1Done || !agreed}>
          {loading ? 'Submitting…' : '🚀 Submit Report'}
        </button>
      </div>
    </div>
  );
}

IncidentPage.propTypes = {
  partner: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    hotel_name: PropTypes.string,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
